<template>
  <vs-popup :active="active" :title="popupTitle" :class="popupClass" @update:active="close">
    <div class="request-form-content">
      <template v-if="isBulkMode">
        <div class="step-shell">
          <div class="step-chip-row">
            <div
              v-for="stepItem in bulkStepItems"
              :key="stepItem.number"
              class="step-chip"
              :class="{
                'step-chip-active': bulkStep === stepItem.number,
                'step-chip-done': bulkStep > stepItem.number
              }"
            >
              {{ stepItem.number }}. {{ stepItem.label }}
            </div>
          </div>

          <div v-show="bulkStep === 1" class="form-panel mb-4">
            <div class="panel-title">Configuração</div>
            <div class="vx-row">
              <div class="vx-col md:w-1/2 w-full mb-4">
                <label class="text-xs text-gray-600 block mb-1">{{ scheduleLabel }}</label>
                <div class="vs-con-input w-full" :class="{'vs-input-danger': errors.has('scheduled_at')}">
                  <flat-pickr
                    v-model="form.scheduled_at"
                    :config="flatpickrConfig"
                    class="w-full flatpickr-theme"
                    name="scheduled_at"
                    placeholder="Selecione data e hora"
                  />
                </div>
                <input type="hidden" name="scheduled_at" :value="form.scheduled_at" :data-vv-as="'Data/hora da execução'" v-validate="requiresSchedule ? 'required' : ''">
                <small v-if="errors.has('scheduled_at')" class="text-danger text-xs">{{ errors.first('scheduled_at') }}</small>
              </div>
              <div class="vx-col md:w-1/2 w-full mb-4">
                <vs-input
                  v-model="form.clickup_link"
                  label="Link do ClickUp"
                  placeholder="https://app.clickup.com/..."
                  name="clickup_link"
                  v-validate="'required|url'"
                  :danger="errors.has('clickup_link')"
                />
                <small v-if="errors.has('clickup_link')" class="text-danger text-xs">{{ errors.first('clickup_link') }}</small>
              </div>
              <div class="vx-col w-full">
                <vs-textarea
                  v-model="form.motivo"
                  label="Motivo global"
                  name="motivo"
                  v-validate="'required'"
                  :danger="errors.has('motivo')"
                />
                <small v-if="errors.has('motivo')" class="text-danger text-xs">{{ errors.first('motivo') }}</small>
              </div>
            </div>
          </div>

          <div v-show="bulkStep === 2" class="form-panel mb-4">
            <div class="panel-title">Importação</div>
            <request-bulk-import
              ref="bulkImport"
              :active="active && isBulkMode"
              :scheduled-at="form.scheduled_at"
              :reason="form.motivo"
              @change="handleBulkChange"
            />
          </div>

          <div v-show="bulkStep === 3" class="form-panel">
            <div class="panel-title">Revisão</div>
            <p class="review-description">{{ bulkReviewDescription }}</p>

            <div class="vx-row mt-3">
              <div class="vx-col sm:w-1/4 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Linhas processadas</span>
                  <strong class="summary-value">{{ bulkPreview.totalRows || 0 }}</strong>
                </div>
              </div>
              <div class="vx-col sm:w-1/4 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Tenants válidos</span>
                  <strong class="summary-value text-success">{{ bulkPreview.validCount || 0 }}</strong>
                </div>
              </div>
              <div class="vx-col sm:w-1/4 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Tenants inválidos</span>
                  <strong class="summary-value text-danger">{{ bulkPreview.invalidCount || 0 }}</strong>
                </div>
              </div>
              <div class="vx-col sm:w-1/4 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Ignorados</span>
                  <strong class="summary-value text-warning">{{ bulkPreview.ignoredCount || 0 }}</strong>
                </div>
              </div>
              <div class="vx-col sm:w-1/2 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Data/hora da execução</span>
                  <strong class="summary-value">{{ form.scheduled_at || 'Não informado' }}</strong>
                </div>
              </div>
              <div class="vx-col sm:w-1/2 w-full mb-3">
                <div class="summary-card">
                  <span class="summary-label">Motivo global</span>
                  <strong class="summary-value multiline">{{ form.motivo || 'Não informado' }}</strong>
                </div>
              </div>
            </div>

            <div class="review-box mt-2">
              <div class="review-box-title">{{ bulkReviewTitle }}</div>
              <div class="review-table-shell">
                <vs-table :data="bulkHasInvalidRows ? bulkInvalidRows : bulkReviewRows">
                  <template slot="thead">
                    <vs-th>Linha</vs-th>
                    <vs-th>Tenant informado</vs-th>
                    <vs-th>Status</vs-th>
                    <vs-th>Detalhe</vs-th>
                  </template>

                  <template slot-scope="{ data }">
                    <vs-tr v-for="item in data" :key="`${item.index}-${item.input}-${item.code}`">
                      <vs-td>{{ item.index }}</vs-td>
                      <vs-td>{{ item.input || '-' }}</vs-td>
                      <vs-td>
                        <vs-chip :color="item.status === 'valid' ? 'success' : 'danger'">
                          {{ item.status === 'valid' ? 'Válido' : 'Inválido' }}
                        </vs-chip>
                      </vs-td>
                      <vs-td>
                        <span v-if="item.status === 'valid'">
                          {{ item.tenant_name }}<span v-if="item.fqdn"> ({{ item.fqdn }})</span>
                        </span>
                        <span v-else>{{ item.message }}</span>
                      </vs-td>
                    </vs-tr>
                  </template>
                </vs-table>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="vx-row">
        <div class="vx-col sm:w-1/2 w-full mb-4">
          <vs-select
            v-model="form.action_type"
            label="Ação"
            class="w-full"
            name="action_type"
            v-validate="'required'"
            :danger="errors.has('action_type')"
          >
            <vs-select-item v-for="option in actionOptions" :key="option.value" :value="option.value" :text="option.label" />
          </vs-select>
          <small v-if="errors.has('action_type')" class="text-danger text-xs">{{ errors.first('action_type') }}</small>
        </div>
        <div class="vx-col sm:w-1/2 w-full mb-4">
          <template v-if="usesFixedTarget">
            <vs-input :value="fixedTenantDisplay" label="Tenant" readonly />
            <input type="hidden" name="target_id" :value="form.target_id" v-validate="'required'" data-vv-as="Tenant" />
          </template>
          <template v-else>
            <label class="text-xs text-gray-600 block mb-1">Tenant</label>
            <div class="flex gap-2">
              <v-select
                class="flex-1"
                :options="filteredTenants"
                :reduce="tenant => tenant.id"
                label="name"
                :clearable="true"
                :filterable="true"
                :loading="tenantLoading"
                :placeholder="tenantPlaceholder"
                v-model="form.target_id"
                @search="onTenantSearch"
                :class="{'vs-input-danger': errors.has('target_id')}"
              >
                <template #option="option">
                  <div class="flex justify-between w-full">
                    <span>{{ tenantDisplay(option) }}</span>
                    <span v-if="option.is_beta" class="text-warning text-xs font-semibold">beta</span>
                  </div>
                </template>
                <template #selected-option="option">
                  <div class="flex items-center">
                    <span>{{ tenantDisplay(option) }}</span>
                    <span v-if="option.is_beta" class="text-warning text-xs font-semibold ml-1">beta</span>
                  </div>
                </template>
              </v-select>
              <input type="hidden" name="target_id" v-model="form.target_id" v-validate="'required'" data-vv-as="Tenant" />
              <vs-button type="border" icon-pack="feather" icon="icon-rotate-ccw" :disabled="tenantLoading" @click="loadTenants" />
            </div>
          </template>
          <small v-if="errors.has('target_id')" class="text-danger text-xs">{{ errors.first('target_id') }}</small>
        </div>
        <div class="vx-col sm:w-1/2 w-full mb-4">
          <label class="text-xs text-gray-600 block mb-1">{{ scheduleLabel }}</label>
          <div class="vs-con-input w-full" :class="{'vs-input-danger': errors.has('scheduled_at')}">
            <flat-pickr
              v-model="form.scheduled_at"
              :config="flatpickrConfig"
              class="w-full flatpickr-theme"
              name="scheduled_at"
              placeholder="Selecione data e hora"
            />
          </div>
          <input type="hidden" name="scheduled_at" :value="form.scheduled_at" :data-vv-as="'Data/hora da execução'" v-validate="requiresSchedule ? 'required' : ''">
          <small v-if="errors.has('scheduled_at')" class="text-danger text-xs">{{ errors.first('scheduled_at') }}</small>
        </div>
        <div class="vx-col sm:w-1/2 w-full mb-4">
          <vs-input
            v-model="form.clickup_link"
            label="Link do ClickUp"
            placeholder="https://app.clickup.com/..."
            name="clickup_link"
            v-validate="'required|url'"
            :danger="errors.has('clickup_link')"
          />
          <small v-if="errors.has('clickup_link')" class="text-danger text-xs">{{ errors.first('clickup_link') }}</small>
        </div>
        <div class="vx-col w-full mb-4">
          <vs-textarea v-model="form.motivo" label="Motivo" name="motivo" v-validate="'required'" :danger="errors.has('motivo')"/>
          <small v-if="errors.has('motivo')" class="text-danger text-xs">{{ errors.first('motivo') }}</small>
        </div>
      </div>
    </div>

    <div v-if="isBulkMode" class="flex justify-between gap-3 mt-4">
      <div>
        <vs-button v-if="bulkStep > 1" type="border" @click="prevBulkStep">Voltar</vs-button>
        <vs-button v-else-if="showBulkModeToggle" type="border" @click="setMode('single')">Agendamento individual</vs-button>
      </div>
      <div class="flex gap-3">
        <vs-button v-if="bulkStep < 3" color="primary" :disabled="!bulkCanGoNext" @click="nextBulkStep">
          Próximo
        </vs-button>
        <vs-button v-else color="primary" :disabled="submitting || !canSubmit" @click="submit">
          Criar agendamentos
        </vs-button>
        <vs-button type="border" @click="close">Cancelar</vs-button>
      </div>
    </div>

    <div v-else class="flex justify-end gap-3 mt-4">
      <vs-button v-if="showBulkModeToggle" type="border" @click="setMode('bulk')">
        Usar importação em massa
      </vs-button>
      <vs-button color="primary" :disabled="submitting || !canSubmit" @click="submit">
        {{ submitLabel }}
      </vs-button>
      <vs-button type="border" @click="close">Cancelar</vs-button>
    </div>
  </vs-popup>
</template>

<script>
import { hasPermissionBackOffice } from '@/utils'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import vSelect from 'vue-select'
import tenantsRequest from '@/http/requests/tenants'
import RequestBulkImport from '@/components/approvals/RequestBulkImport.vue'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'RequestFormModal',
  components: {
    flatPickr,
    vSelect,
    RequestBulkImport
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    request: {
      type: Object,
      default: null
    },
    fixedTargetId: {
      type: Number,
      default: null
    },
    fixedTenantLabel: {
      type: String,
      default: ''
    },
    allowBulkImport: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        id: null,
        action_type: 'tenant_deactivation',
        target_id: '',
        scheduled_at: '',
        motivo: '',
        clickup_link: ''
      },
      submitting: false,
      actionOptions: [
        { value: 'tenant_deactivation', label: 'Agendar Desativação' }
      ],
      tenants: [],
      tenantLoading: false,
      tenantSearch: '',
      tenantSelected: null,
      mode: 'single',
      bulkStep: 1,
      bulkPreview: {
        ready: false,
        fileName: '',
        totalRows: 0,
        validCount: 0,
        invalidCount: 0,
        ignoredCount: 0,
        items: [],
        validTenants: [],
        canSubmit: false
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.form.id;
    },
    usesFixedTarget() {
      return Number.isInteger(this.fixedTargetId) && this.fixedTargetId > 0;
    },
    fixedTenantDisplay() {
      if (this.fixedTenantLabel) { return this.fixedTenantLabel; }
      return this.usesFixedTarget ? `ID ${this.fixedTargetId}` : '';
    },
    requiresSchedule() {
      return this.form.action_type === 'tenant_deactivation';
    },
    scheduleLabel() {
      return `Data/hora da execução ${this.requiresSchedule ? '(obrigatório)' : ''}`;
    },
    canSubmit() {
      if (!this.hasSubmitPermission) {
        return false;
      }

      if (this.isBulkMode) {
        return !!this.bulkPreview.canSubmit;
      }

      return true;
    },
    hasSubmitPermission() {
      return this.isEdit ? hasPermissionBackOffice('requests-update') : hasPermissionBackOffice('requests');
    },
    flatpickrConfig() {
      const min = new Date();
      min.setMinutes(min.getMinutes() + 2);
      return {
        enableTime: true,
        dateFormat: "Y-m-d H:i",
        time_24hr: true,
        minDate: min
      }
    },
    hostIsBeta() {
      return window.location.host.includes('beta');
    },
    filteredTenants() {
      return this.tenants.filter(tenant => {
        if (this.hostIsBeta) { return !!tenant.is_beta; }
        return tenant.is_beta === false;
      });
    },
    tenantPlaceholder() {
      return this.hostIsBeta ? 'Selecione um tenant beta' : 'Selecione um tenant';
    },
    showBulkModeToggle() {
      return this.allowBulkImport && !this.isEdit && !this.usesFixedTarget && this.form.action_type === 'tenant_deactivation';
    },
    isBulkMode() {
      return this.showBulkModeToggle && this.mode === 'bulk';
    },
    popupTitle() {
      if (this.isEdit) {
        return 'Editar agendamento';
      }

      if (this.isBulkMode) {
        return 'Novo agendamento em massa';
      }

      return 'Novo agendamento';
    },
    submitLabel() {
      if (this.isEdit) {
        return 'Salvar alterações';
      }

      return this.isBulkMode ? 'Criar agendamentos' : 'Criar agendamento';
    },
    bulkStepItems() {
      return [
        { number: 1, label: 'Configurar' },
        { number: 2, label: 'Importar' },
        { number: 3, label: 'Revisar e confirmar' }
      ];
    },
    isBulkConfigurationFilled() {
      return !!(
        (this.form.scheduled_at || '').trim() &&
        (this.form.clickup_link || '').trim() &&
        (this.form.motivo || '').trim()
      );
    },
    bulkCanGoNext() {
      if (this.bulkStep === 1) {
        return this.isBulkConfigurationFilled;
      }

      if (this.bulkStep === 2) {
        return !!this.bulkPreview.ready;
      }

      return false;
    },
    bulkReviewRows() {
      return Array.isArray(this.bulkPreview.items) ? this.bulkPreview.items : [];
    },
    bulkInvalidRows() {
      return this.bulkReviewRows.filter(item => item.status !== 'valid');
    },
    bulkHasInvalidRows() {
      return this.bulkInvalidRows.length > 0;
    },
    bulkReviewTitle() {
      return this.bulkHasInvalidRows ? 'Pendências encontradas' : 'Tudo pronto para confirmar';
    },
    bulkReviewDescription() {
      if (this.bulkHasInvalidRows) {
        return 'Existem tenants inválidos na planilha. Corrija a planilha e importe novamente antes de confirmar.';
      }

      return 'Os tenants abaixo estão válidos e serão agendados com a configuração informada.';
    },
    popupClass() {
      return this.isBulkMode ? 'request-form-popup request-form-popup-bulk' : 'request-form-popup';
    }
  },
  watch: {
    request: {
      handler() {
        this.applyRequest();
      },
      deep: true,
      immediate: true
    },
    active(val) {
      if (val) {
        this.applyRequest();
        if (!this.usesFixedTarget && !this.isBulkMode) {
          this.loadTenants();
        }
      }
    },
    fixedTargetId() {
      if (!this.isEdit) {
        this.form.target_id = this.usesFixedTarget ? this.fixedTargetId : '';
      }
    }
  },
  methods: {
    applyRequest() {
      if (this.request) {
        this.mode = 'single';
        this.bulkStep = 1;
        this.form = {
          id: this.request.id,
          action_type: this.request.action_type,
          target_id: this.usesFixedTarget ? this.fixedTargetId : this.request.target_id,
          scheduled_at: this.request.scheduled_at ? this.request.scheduled_at.substring(0, 16) : '',
          motivo: this.request.payload && this.request.payload.motivo ? this.request.payload.motivo : '',
          clickup_link: this.request.clickup_link
        };
        this.bulkPreview = this.defaultBulkPreview();
      } else {
        this.resetForm();
      }
    },
    defaultBulkPreview() {
      return {
        ready: false,
        fileName: '',
        totalRows: 0,
        validCount: 0,
        invalidCount: 0,
        ignoredCount: 0,
        items: [],
        validTenants: [],
        canSubmit: false
      };
    },
    resetForm() {
      this.mode = 'single';
      this.bulkStep = 1;
      this.form = {
        id: null,
        motivo: '',
        scheduled_at: '',
        clickup_link: '',
        action_type: 'tenant_deactivation',
        target_id: this.usesFixedTarget ? this.fixedTargetId : ''
      };
      this.bulkPreview = this.defaultBulkPreview();
    },
    async submit() {
      if (!this.hasSubmitPermission) {
        this.$vs.notify({ color: 'warning', title: 'Permissão', text: 'Você não tem permissão.' });
        return;
      }

      if (this.requiresSchedule && !this.form.scheduled_at) {
        this.$vs.notify({
          color: 'warning',
          title: 'Campos obrigatórios',
          text: 'Informe a data/hora da execução antes de salvar.'
        });
        return;
      }

      const valid = await this.$validator.validateAll();
      if (!valid) {
        const msg = this.errors.items.length ? this.errors.items[0].msg : 'Revise os campos obrigatórios.';
        this.$vs.notify({ color: 'warning', title: 'Campos obrigatórios', text: msg });
        return;
      }

      if (this.isBulkMode) {
        if (this.bulkStep !== 3) {
          return;
        }

        if (!this.bulkPreview.ready) {
          this.$vs.notify({
            color: 'warning',
            title: 'Planilha',
            text: 'Importe a planilha de tenants antes de continuar.'
          });
          return;
        }

        if (this.bulkPreview.invalidCount > 0 || !this.bulkPreview.canSubmit) {
          this.$vs.notify({
            color: 'warning',
            title: 'Planilha',
            text: 'Corrija os tenants inválidos da planilha antes de confirmar o agendamento em massa.'
          });
          return;
        }
      }

      this.submitting = true;

      try {
        let successText = 'Agendamento salvo com sucesso.';

        if (this.isEdit) {
          const payload = {
            action_type: this.form.action_type,
            target_id: Number(this.form.target_id),
            scheduled_at: this.form.scheduled_at || null,
            payload: { motivo: this.form.motivo },
            clickup_link: this.form.clickup_link
          };

          await this.$store.dispatch('approvals/updateRequest', { id: this.form.id, data: payload });
        } else if (this.isBulkMode) {
          const response = await this.$store.dispatch('approvals/createBulkRequest', {
            action_type: this.form.action_type,
            scheduled_at: this.form.scheduled_at || null,
            payload: { motivo: this.form.motivo },
            clickup_link: this.form.clickup_link,
            tenants: this.bulkPreview.validTenants
          });

          successText = `${response.created_count || this.bulkPreview.validTenants.length} agendamentos criados com sucesso.`;
        } else {
          const payload = {
            action_type: this.form.action_type,
            target_id: Number(this.form.target_id),
            scheduled_at: this.form.scheduled_at || null,
            payload: { motivo: this.form.motivo },
            clickup_link: this.form.clickup_link
          };

          await this.$store.dispatch('approvals/createRequest', payload);
        }

        this.$vs.notify({ color: 'success', title: 'Agendamento', text: successText });
        this.$emit('saved');
        this.close();
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Erro ao salvar solicitação.';
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        this.submitting = false;
      }
    },
    close() {
      this.$emit('update:active', false);
      this.resetForm();
      this.$nextTick(() => {
        this.$validator.reset();
      });
    },
    async setMode(mode) {
      this.mode = mode;
      this.bulkStep = 1;
      this.bulkPreview = this.defaultBulkPreview();

      if (this.isBulkMode) {
        this.form.target_id = '';
      } else if (!this.usesFixedTarget && this.tenants.length === 0) {
        await this.loadTenants();
      }

      this.$nextTick(() => {
        this.$validator.reset();
      });
    },
    handleBulkChange(preview) {
      this.bulkPreview = preview || this.defaultBulkPreview();
    },
    async nextBulkStep() {
      if (!this.isBulkMode || this.bulkStep >= 3) {
        return;
      }

      if (this.bulkStep === 1) {
        const valid = await this.$validator.validateAll();
        if (!valid) {
          const msg = this.errors.items.length ? this.errors.items[0].msg : 'Revise os campos obrigatórios.';
          this.$vs.notify({ color: 'warning', title: 'Campos obrigatórios', text: msg });
          return;
        }
      }

      if (this.bulkStep === 2 && !this.bulkPreview.ready) {
        this.$vs.notify({
          color: 'warning',
          title: 'Planilha',
          text: 'Importe a planilha antes de avançar.'
        });
        return;
      }

      this.bulkStep += 1;
    },
    prevBulkStep() {
      if (!this.isBulkMode || this.bulkStep <= 1) {
        return;
      }

      this.bulkStep -= 1;
    },
    tenantDisplay(option) {
      if (!option) return '';
      const prefix = option.fqdn ? option.fqdn.split('.')[0] : option.id;
      const name = option.name || prefix;
      return `${name} (${prefix})`;
    },
    async onTenantSearch(search) {
      this.tenantSearch = search || '';
      await this.loadTenants();
    },
    async loadTenants() {
      try {
        this.tenantLoading = true;
        const response = await tenantsRequest.fetchPaginatedTenants(
          localStorage.getItem('accessToken'),
          true,
          1,
          50,
          this.tenantSearch || '',
          'fqdn',
          'ASC'
        );
        const data = response.data || {};
        const collection = data.items || data.data || data || [];
        this.tenants = collection.map(item => ({
          ...item,
          name: item.name || (item.fqdn ? item.fqdn.split('.')[0] : `ID ${item.id}`)
        }));
      } catch (error) {
        console.error(error);
        this.$vs.notify({ color: 'danger', title: 'Tenants', text: 'Não foi possível carregar tenants.' });
      } finally {
        this.tenantLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.request-form-popup ::v-deep .vs-popup {
  width: 640px;
  max-width: calc(100vw - 24px);
}

.request-form-popup-bulk ::v-deep .vs-popup {
  width: 760px;
  max-width: calc(100vw - 24px);
}

.request-form-content {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.form-panel {
  height: 100%;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #ffffff;
}

.panel-title {
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.step-shell {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.step-chip {
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 700;
}

.step-chip-active {
  background: #111827;
  color: #ffffff;
}

.step-chip-done {
  background: #dcfce7;
  color: #166534;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: #fbfbfb;
  min-height: 88px;
}

.summary-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
}

.summary-value {
  color: #111827;
  line-height: 1.4;
}

.summary-value.multiline {
  white-space: pre-wrap;
  word-break: break-word;
}

.review-description {
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
}

.review-box {
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #ffffff;
}

.review-box-title {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.review-table-shell {
  max-height: 280px;
  overflow-y: auto;
}

.flatpickr-theme ::v-deep .flatpickr-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: #f8f8f8;
  color: inherit;
  height: 40px;
}

.flatpickr-theme ::v-deep .flatpickr-input:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: 0 0 0 1px #7367f0;
}

.vs-input-danger .flatpickr-theme ::v-deep .flatpickr-input {
  border-color: #f74f64;
}

@media (max-width: 991px) {
  .request-form-popup-bulk ::v-deep .vs-popup {
    width: calc(100vw - 24px);
  }
}
</style>
